import { type SugarProviderPhoto } from '@app/types'
import { Alert, Button, Group, Modal, Stack, Text, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconAlertTriangle, IconRefresh, IconX } from '@tabler/icons-react'
import { type FC } from 'react'

export type RemoveIntent = 'remove' | 'replace'

export interface PhotoRemoveConfirmModalProps {
  intent: RemoveIntent
  isPending?: boolean
  onCancel: () => void
  onConfirm: () => void
  opened: boolean
  photo: SugarProviderPhoto | null
}

export const PhotoRemoveConfirmModal: FC<PhotoRemoveConfirmModalProps> = ({
  intent,
  isPending = false,
  onCancel,
  onConfirm,
  opened,
  photo
}) => {
  const theme = useMantineTheme()
  const fullScreen = !!useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isActive = photo?.status === 'Active'
  const title = intent === 'replace' ? 'Replace this photo?' : 'Remove this photo?'
  const confirmLabel = intent === 'replace' ? 'Replace' : 'Remove'

  return (
    <Modal centered fullScreen={fullScreen} onClose={onCancel} opened={opened} title={title}>
      <Stack>
        {isActive ? (
          <Alert color='yellow' icon={<IconAlertTriangle size={18} />}>
            This photo is currently visible on find.dpp.org. It will be removed within 6 hours.
          </Alert>
        ) : (
          <Text size='sm'>
            {intent === 'replace'
              ? 'The current photo will be removed and replaced with the one you select next.'
              : 'This photo will be removed from your profile.'}
          </Text>
        )}
        <Group position='right' spacing='xs'>
          <Button disabled={isPending} onClick={onCancel} variant='default'>Cancel</Button>
          <Button
            color='red'
            leftIcon={intent === 'replace' ? <IconRefresh size={18} /> : <IconX size={18} />}
            loading={isPending}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
